import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, H3, InputLabel, InputWithUnit, Select, TextArea, TextInput } from '@components/Common';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';
import FlowMeterTypeSelect from './FlowMeterTypeSelect';

// TanStack Query hooks
import { useCustomersQuery, Customer } from '@api/queries/customers';
import { useProductsQuery, Product } from '@api/queries/products';
import { useTerminalsQuery, Terminal } from '@api/queries/terminals';
import {
  useCreateTankMutation,
  useUpdateTankMutation,
  useDeleteTankMutation,
  Tank,
  AddTankCmd,
  UpdateTankCmd,
  FlowMeterType
} from '@api/queries/tanks';

const Warning = styled.div`
  padding: 5px;
  font-size: 10px;
  color: #9f6000;
  background-color: #feefb3;
`;

const RemoveButton = styled(Button)`
  margin-right: 15px;
`;

const colourStyles = {
  multiValueLabel: (styles: any, obj: { data: { isActive: boolean } }) => ({
    backgroundColor: obj.data.isActive ? 'inherit' : '#feefb3',
    color: obj.data.isActive ? 'inherit' : '#9f6000'
  })
};

interface Props {
  tankToEdit?: Tank | null;
  onClose: () => void;
}

const defaultTank: Tank = {
  id: null,
  name: '',
  notes: '',
  capacity: 0,
  baseLevel: null,
  levelFactor: null,
  customers: [],
  terminalId: null,
  productId: null,
  isEnabled: true,
  flowMeterType: FlowMeterType.None
};

const TankEditor: React.FC<Props> = ({ tankToEdit, onClose }) => {
  const { t } = useTranslation();

  // Local form state
  const [tank, setTank] = useState<Tank>({ ...(tankToEdit || defaultTank) });
  const [isFormChanged, setIsFormChanged] = useState(false);

  // TanStack Query for reference data
  const { data: customers = [], isLoading: isLoadingCustomers } = useCustomersQuery();
  const { data: products = [], isLoading: isLoadingProducts } = useProductsQuery();
  const { data: terminals = [] } = useTerminalsQuery();

  // TanStack Query mutations
  const createTankMutation = useCreateTankMutation();
  const updateTankMutation = useUpdateTankMutation();
  const deleteTankMutation = useDeleteTankMutation();

  const isLoading = isLoadingCustomers || isLoadingProducts;
  const isSaving = createTankMutation.isPending || updateTankMutation.isPending || deleteTankMutation.isPending;

  const isEdit = tankToEdit !== null;

  const handleChange = (newValue: any, propName: keyof Tank) => {
    setTank(prev => ({ ...prev, [propName]: newValue }));
    setIsFormChanged(true);
  };

  const handleSelectChange = (newValue: any, actionMeta: any, propName: keyof Tank) => {
    const value = actionMeta.action === 'clear' ? null : newValue.value;
    handleChange(value, propName);
  };

  const customerSelectionOnChange = (selectedCustomers: any, actionMeta: any) => {
    const value =
      actionMeta.action === 'clear'
        ? []
        : selectedCustomers.map((i: any) => ({ customerName: i.label, customerId: i.value }));

    setTank(prev => ({ ...prev, customers: value }));
    setIsFormChanged(true);
  };

  const onFlowMeterTypeChanged = (newValue: FlowMeterType) => {
    handleChange(newValue, 'flowMeterType');
  };

  const isValid = () => {
    const { name, capacity, terminalId } = tank;
    return capacity > 0 && name.trim().length >= 2 && terminalId !== null;
  };

  const onSubmit = (e: React.FormEvent<any>) => {
    e.preventDefault();

    const tankCmd: AddTankCmd = {
      tankName: tank.name,
      capacity: tank.capacity,
      baseLevel: tank.baseLevel,
      levelFactor: tank.levelFactor,
      customerIds: tank.customers?.map(i => i.customerId) || [],
      notes: tank.notes,
      terminalId: tank.terminalId,
      productId: tank.productId,
      flowMeterType: tank.flowMeterType
    };

    if (!isEdit) {
      createTankMutation.mutate(tankCmd, { onSuccess: () => onClose() });
    } else {
      const updateCmd: UpdateTankCmd = { ...tankCmd, id: tank.id };
      updateTankMutation.mutate(updateCmd, { onSuccess: () => onClose() });
    }
  };

  const onRemove = () => {
    if (confirm(t('tank.confirm_delete'))) {
      deleteTankMutation.mutate(tank.id!, { onSuccess: () => onClose() });
    }
  };

  // Prepare options for selects
  const mapCustomersToOptions = (c: Customer) => ({ value: c.id, label: c.name, isActive: c.isActive });

  let activeCustomers = customers.filter(c => c.isActive).map(mapCustomersToOptions);
  let selectedCustomers: any[] = [];

  const terminalOptions = terminals.map((i: Terminal) => ({ value: i.id, label: i.name }));
  const productOptions = products
    .filter((i: Product) => i.isEnabled || i.id === tank.productId)
    .map((i: Product) => ({ value: i.id, label: `${i.name} (${i.code})` }));

  if (tankToEdit !== null && tankToEdit.customers) {
    activeCustomers = activeCustomers.concat(
      customers
        .filter(
          c => tankToEdit.customers!.find(i => i.customerId === c.id) && !activeCustomers.find(i => i.value === c.id)
        )
        .map(mapCustomersToOptions)
    );

    selectedCustomers = activeCustomers.filter(i => tankToEdit.customers!.find(j => j.customerId === i.value));
  }

  const selectedProduct = productOptions.find(i => i.value === tank.productId) || null;
  const selectedTerminal = terminalOptions.find(i => i.value === tank.terminalId) || null;

  const isSaveDisabled = isSaving || !isFormChanged || !isValid();
  const isProductDisabled = isEdit && tankToEdit?.balance && tankToEdit.balance.weight !== 0;

  return (
    <SideForm isLoading={isLoading} onEscape={onClose} onSubmit={onSubmit}>
      {isEdit ? (
        <H3>
          {t('editing')}: <i>{tankToEdit?.name}</i>
        </H3>
      ) : (
        <H3>
          {t('add_new')} {t('tank.tank').toLowerCase()}
        </H3>
      )}
      <InputLabel htmlFor="name">{t('tank.name')} *</InputLabel>
      <TextInput
        id="name"
        name="name"
        type="text"
        autoFocus
        value={tank.name}
        onChange={e => handleChange(e.target.value, 'name')}
      />
      <InputLabel htmlFor="capacity">{t('tank.capacity')} *</InputLabel>
      <InputWithUnit>
        <TextInput
          id="capacity"
          name="capacity"
          type="number"
          step="any"
          value={tank.capacity}
          onChange={e => handleChange(e.target.valueAsNumber, 'capacity')}
        />
        <span>
          m<sup>3</sup>
        </span>
      </InputWithUnit>
      <InputLabel htmlFor="baseLevel">{t('tank.base_level')}</InputLabel>
      <InputWithUnit>
        <TextInput
          id="baseLevel"
          name="baseLevel"
          type="number"
          step="any"
          value={tank.baseLevel ?? ''}
          onChange={e => handleChange(e.target.valueAsNumber, 'baseLevel')}
        />
        <span>
          m<sup>3</sup>
        </span>
      </InputWithUnit>
      <InputLabel htmlFor="levelFactor">{t('tank.level_factor')}</InputLabel>
      <InputWithUnit>
        <TextInput
          id="levelFactor"
          name="levelFactor"
          type="number"
          step="any"
          value={tank.levelFactor ?? ''}
          onChange={e => handleChange(e.target.valueAsNumber, 'levelFactor')}
        />
        <span>
          m<sup>3</sup>/mm
        </span>
      </InputWithUnit>
      <FlowMeterTypeSelect flowMeterType={tank.flowMeterType} onChange={onFlowMeterTypeChanged} />
      <InputLabel htmlFor="customerId">{t('tank.customer')}</InputLabel>
      <Select
        defaultValue={selectedCustomers}
        isMulti={true}
        name="customerId"
        placeholder={t('select_customer')}
        onChange={customerSelectionOnChange}
        options={activeCustomers}
        styles={colourStyles}
      />
      {selectedCustomers.find(i => i.isActive === false) && <Warning>{t('tank.customer_is_inactive')}</Warning>}
      <InputLabel htmlFor="terminalId">{t('tank.terminal')} *</InputLabel>
      <Select
        name="terminalId"
        placeholder={t('select_terminal')}
        isClearable={false}
        value={selectedTerminal}
        onChange={(i, j) => handleSelectChange(i, j, 'terminalId')}
        options={terminalOptions}
      />
      <InputLabel htmlFor="productId">{t('tank.product')}</InputLabel>
      <Select
        name="productId"
        placeholder={t('select_product')}
        value={selectedProduct}
        onChange={(i, j) => handleSelectChange(i, j, 'productId')}
        options={productOptions}
        isDisabled={isProductDisabled}
      />
      {isProductDisabled && <Warning>{t('tank.tank_not_empty')}</Warning>}
      <InputLabel htmlFor="notes">{t('tank.notes')}</InputLabel>
      <TextArea
        id="notes"
        name="notes"
        value={tank.notes || ''}
        onChange={e => handleChange(e.target.value, 'notes')}
      />
      <SidebarButtons>
        <Button type="button" secondary={true} disabled={isSaving} onClick={onClose}>
          {t('cancel')}
        </Button>
        <div />
        {isSaving && <OperationLoader title={t('saving') + '...'} />}
        {isEdit && (
          <RemoveButton type="button" danger={true} disabled={isSaving} onClick={onRemove}>
            {t('delete')}
          </RemoveButton>
        )}
        <Button type="submit" tertiary={true} disabled={isSaveDisabled}>
          {t('save')}
        </Button>
      </SidebarButtons>
    </SideForm>
  );
};

export default TankEditor;
